"use client"
import { useRef, type FC, type ReactNode } from "react"
import { styled } from "styled-system/jsx"

/**
 * `anchor-scope` confines the single static anchor name to each trigger's
 * subtree, so a single name never collides across tooltips on the page.
 */
const TooltipTrigger = styled("span", {
  base: {
    display: "inline-flex",
    anchorName: "--tooltip",
    anchorScope: "--tooltip",
  },
})

const TooltipContent = styled("div", {
  base: {
    position: "fixed",
    inset: "auto",
    //Shows the arrow
    overflow: "visible",
    //Wires up the positioning of the tooltip
    positionAnchor: "--tooltip",
    bottom: "[anchor(top)]",
    left: "[anchor(center)]",
    translate: "[-50% 0]",
    backgroundColor: "text",
    color: "text.onDark",
    paddingInline: "sm",
    paddingBlock: "xs",
    rounded: "md",
    maxWidth: "[260px]",
    boxShadow: "light.md",
  },
})

/** A real child element rather than a pseudo-element, so it can overflow. */
const TooltipArrow = styled("span", {
  base: {
    position: "absolute",
    top: "[100%]",
    left: "[50%]",
    translate: "[-50% -50%]",
    width: "[8px]",
    height: "[8px]",
    backgroundColor: "text",
    rotate: "[45deg]",
  },
})

export type TooltipProps = {
  content: ReactNode
  children: ReactNode
  /**
   * id of the bubble. Set the same value as `aria-describedby` on the trigger
   * control so screen readers announce the content when it gains focus.
   */
  id?: string
}

/**
 * Lightweight tooltip built on the native Popover API + CSS anchor positioning.
 * Chromium-only (`anchor()` / `position-anchor` / `anchor-scope` are not yet in
 * Firefox or Safari) — acceptable for this internal back-office. No portal, no
 * JS positioning: a single anchor name is confined to each trigger's subtree
 * via `anchor-scope`, so tooltips never collide. The trigger span also works
 * around `aria-disabled` triggers, which still emit hover.
 */
export const Tooltip: FC<TooltipProps> = ({ content, children, id }) => {
  const popoverRef = useRef<HTMLDivElement>(null)

  const open = () => {
    if (popoverRef.current && !popoverRef.current.matches(":popover-open")) {
      popoverRef.current.showPopover()
    }
  }
  const close = () => {
    if (popoverRef.current?.matches(":popover-open")) {
      popoverRef.current.hidePopover()
    }
  }

  return (
    <TooltipTrigger
      onPointerEnter={open}
      onPointerLeave={close}
      onFocus={open}
      onBlur={close}
    >
      {children}
      <TooltipContent
        ref={popoverRef}
        //Screen reader thing. When the trigger is focused, the content is announced.
        id={id}
        popover="manual"
        role="tooltip"
      >
        {content}
        <TooltipArrow />
      </TooltipContent>
    </TooltipTrigger>
  )
}
